// src/onboarding/pages/PersonalizationPage.jsx
//
// Route /tenant-admin/personalization (RequireTenantAdmin). The panel is DERIVED from the store every
// render (onboardingModel.panelOf) — the page keeps only a local `step` override ('form' after
// "Esegui di nuovo" or "Riavvia"). v196: ONE form (PersonalizationForm) replaces the two of v191.
// ⛔ It never auto-jumps on success: it follows the store. Skip leaves to the Content Generator; the banner is the way back.
// v192 (BE 178d473): a start refused or crashed AFTER its 202 (`last_start_error`) is said HERE, above
// every panel — a refused RE-run lands on the completed panel, not on the form. The full RunRefusalNotice
// only right after THIS tab's own start (the outcome carries the code); an older error is one dated,
// past-tense line, because credits or the plan may have changed since.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useOnboarding } from '../onboardingStore.js';
import { panelOf, viewOf, pendingBaseline, lastStartErrorOf } from '../onboardingModel.js';
import { startErrorReason } from '../copy.js';
import { formatDate } from '../ui/formatDate.js';
import RunRefusalNotice from '../../components/credits/RunRefusalNotice.jsx';
import { refusalFromError } from '../../utils/runRefusal.js';
import SectionCard, { CARD_BODY, PAGE_COLUMN } from '../../components/ui/SectionCard.jsx';
import { SEMANTIC } from '../../constants/theme.js';
import { HINT, NOTE } from '../ui/formStyles.js';
import PersonalizationForm from '../ui/PersonalizationForm.jsx';
import ProgressView from '../ui/ProgressView.jsx';

export default function PersonalizationPage() {
  const { t, i18n } = useTranslation('onboarding');
  const language = i18n.language?.split('-')[0] === 'en' ? 'en' : 'it';
  const navigate = useNavigate();
  const { activeTenant } = useAuth();
  const tenantId = activeTenant?.tenant_id || null;
  const snapshot = useOnboarding();
  const [step, setStep] = useState(null);

  useEffect(() => { setStep(null); }, [tenantId]);

  const panel = panelOf(snapshot, tenantId, step);

  // Once the store shows the start (or the run), forget the local form override, so a finished
  // run lands on its own panel instead of reopening the form. panelOf checks pendingStart / running
  // before `step`, so the reset is invisible.
  useEffect(() => { if (panel === 'progress' || panel === 'starting') setStep(null); }, [panel]);
  const view = viewOf(snapshot.status);
  const skip = () => navigate('/content-generator');

  const idle = panel !== 'progress' && panel !== 'starting' && panel !== 'loading';
  const startError = idle ? lastStartErrorOf(snapshot.status) : null;
  const outcome = snapshot.outcome;
  const freshRefusal = startError && outcome?.kind === 'startRefused' && outcome.code === startError.code
    ? refusalFromError({ code: startError.code, status: null })
    : null;
  const unconfirmed = idle && outcome?.kind === 'startUnconfirmed';

  return (
    <div style={PAGE_COLUMN}>
      <SectionCard
        as="section" icon={Sparkles} headingLevel={1} title={t('page.title')}
        subtitle={t('page.subtitle')}
      >
        <div style={CARD_BODY}>
          {startError && (
            <div role="alert" style={NOTE(SEMANTIC.attenzione)}>
              {t('page.startError', {
                date: formatDate(startError.at, language),
                reason: startErrorReason(t, startError.code),
              })}
            </div>
          )}
          {freshRefusal && <div style={{ marginBottom: '16px' }}><RunRefusalNotice refusal={freshRefusal} /></div>}
          {unconfirmed && <div role="status" style={NOTE(SEMANTIC.attenzione)}>{t('page.startUnconfirmed')}</div>}
          {panel === 'form' && view === 'failed' && (
            <div role="status" style={NOTE(SEMANTIC.attenzione)}>{t('progress.failedNote')}</div>
          )}
          {panel === 'form' && (
            <PersonalizationForm
              tenantId={tenantId}
              status={snapshot.status}
              baseline={pendingBaseline(snapshot.status)}
              onSkip={skip}
            />
          )}
          {panel !== 'form' && (
            <ProgressView
              panel={panel} status={snapshot.status} tenantId={tenantId}
              onRestart={() => setStep('form')} onRunAgain={() => setStep('form')}
            />
          )}
          {panel === 'form' && <p style={HINT}>{t('page.skipHint')}</p>}
        </div>
      </SectionCard>
    </div>
  );
}
